import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  AlertCircle,
  CheckCircle,
  MoreVertical,
  Filter,
  Receipt,
  ShoppingCart,
  CreditCard
} from 'lucide-react';
import { salesApi } from '@/api/salesApi';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { useToast } from '@/hooks/useToast';
import { RecordSaleModal } from '@/components/sales/RecordSaleModal';
import { EntityLink } from '@/components/detail/EntityLink';

export function Sales() {
  const queryClient = useQueryClient();
  const { success } = useToast();
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: sales, isLoading } = useQuery({
    queryKey: ['sales'],
    queryFn: salesApi.getAll,
  });

  const { data: unpaidTotal } = useQuery({
    queryKey: ['unpaid-commissions'],
    queryFn: salesApi.getTotalUnpaidCommissions,
  });

  const markAsPaidMutation = useMutation({
    mutationFn: salesApi.markAsPaid,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['unpaid-commissions'] });
      success('Payment Recorded', 'Commission has been marked as paid.', 4000);
    },
  });

  const displayedSales = showUnpaidOnly 
    ? sales?.filter(s => !s.isPaidToLocation)
    : sales;

  const totalRevenue = displayedSales?.reduce((sum, s) => sum + (s.totalSaleAmount || 0), 0) || 0;
  const totalCommissions = displayedSales?.reduce((sum, s) => sum + (s.totalCommission || 0), 0) || 0;
  const totalRetained = displayedSales?.reduce((sum, s) => sum + (s.retainedEarnings || 0), 0) || 0;

  const handleSuccess = () => {
    success(
      'Sale Recorded',
      'Your sale has been recorded and inventory updated.',
      5000
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-warm-cream">Sales</h1>
          <p className="text-gray-600 dark:text-warm-sand mt-1">
            Track sales and manage commissions
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary bg-green-500 hover:bg-green-600 focus:ring-green-500"
        >
          <Plus className="w-5 h-5 mr-2" />
          Record Sale
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Receipt className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Total Revenue</p>
              <p className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-450/10 flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-amber-450" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Commissions</p>
              <p className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">
                {formatCurrency(totalCommissions)}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-danger-500/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-danger-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Unpaid Commissions</p>
              <p className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">
                {formatCurrency(unpaidTotal || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-xs text-gray-600 dark:text-warm-sand uppercase tracking-wide">Total Sales</p>
              <p className="font-display text-xl font-bold text-gray-900 dark:text-warm-cream">
                {displayedSales?.length || 0}
              </p>
            </div>
            <div className="h-8 w-px bg-gray-100 dark:bg-charcoal-700" />
            <div>
              <p className="text-xs text-gray-600 dark:text-warm-sand uppercase tracking-wide">Net Retained</p>
              <p className="font-display text-xl font-bold text-success-400">
                {formatCurrency(totalRetained)}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowUnpaidOnly(!showUnpaidOnly)}
            className={`
              px-4 py-2 rounded-xl font-medium transition-all border
              ${showUnpaidOnly 
                ? 'bg-amber-450/20 text-amber-450 border-amber-450/50' 
                : 'bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand border-gray-300 dark:border-charcoal-600 hover:border-charcoal-500'
              }
            `}
          >
            <Filter className="w-4 h-4 mr-2 inline" />
            {showUnpaidOnly ? 'Showing Unpaid Only' : 'Show All Sales'}
          </button>
        </div>
      </div>

      {/* Sales Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Location</th>
              <th>Qty</th>
              <th>Total</th>
              <th>Commission</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-gray-600 dark:text-warm-sand">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-amber-450/30 border-t-amber-450 rounded-full animate-spin" />
                    Loading sales...
                  </div>
                </td>
              </tr>
            ) : displayedSales?.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-charcoal-700 flex items-center justify-center">
                      <Receipt className="w-8 h-8 text-gray-600 dark:text-warm-sand" />
                    </div>
                    <div>
                      <p className="text-gray-900 dark:text-warm-cream font-medium text-lg">No sales recorded yet</p>
                      <p className="text-gray-600 dark:text-warm-sand text-sm mt-1">
                        Record your first sale to start tracking
                      </p>
                    </div>
                    <button 
                      onClick={() => setShowAddModal(true)}
                      className="btn-primary bg-green-500 hover:bg-green-600 mt-2"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Record Sale
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              displayedSales?.map((sale) => (
                <tr key={sale.id} className={!sale.isPaidToLocation ? 'bg-amber-450/5' : ''}>
                  <td className="text-gray-600 dark:text-warm-sand whitespace-nowrap">
                    {formatDate(sale.saleDate)}
                  </td>
                  <td>
                    <div>
                      <EntityLink type="product" id={sale.productId} label={sale.productName} />
                      <p className="text-xs text-gray-600 dark:text-warm-sand mono">{sale.productVariantSku}</p>
                    </div>
                  </td>
                  <td>
                    <EntityLink type="location" id={sale.locationId} label={sale.locationName} />
                  </td>
                  <td className="text-gray-900 dark:text-warm-cream font-medium">{sale.quantity}</td>
                  <td className="font-medium text-gray-900 dark:text-warm-cream">
                    {formatCurrency(sale.totalSaleAmount)}
                  </td>
                  <td className="text-amber-450">
                    {formatCurrency(sale.totalCommission)}
                  </td>
                  <td>
                    {sale.isPaidToLocation ? (
                      <span className="badge-success">
                        <CheckCircle className="w-3 h-3 mr-1 inline" />
                        Paid
                      </span>
                    ) : (
                      <span className="badge-warning">
                        <AlertCircle className="w-3 h-3 mr-1 inline" />
                        Unpaid
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!sale.isPaidToLocation && (
                        <button
                          onClick={() => markAsPaidMutation.mutate(sale.id)}
                          disabled={markAsPaidMutation.isPending}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-charcoal-700 hover:bg-green-500/20 hover:text-green-600 dark:text-green-400 text-gray-600 dark:text-warm-sand rounded-lg transition-colors text-xs font-medium"
                        >
                          Mark Paid
                        </button>
                      )}
                      <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors">
                        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Record Sale Modal */}
      <RecordSaleModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
